import React from "react";
import { AuthService } from "src/auth/auth-service";
import invokeLambda from "src/lambda/transactionSearchLambda/invoke-lambda";
import TransactionSearchBar from "./transaction-search-bar";
import PayloadDisplay from "src/lambda/transactionSearchLambda/payload-display";
import { Alert, Grid, SpaceBetween } from "@amzn/awsui-components-console";
import { Auth } from "aws-amplify";
import "src/index.scss";
import { InitDomain } from "src/context/domain/domainContext";
import { Domain, RegionalDomain } from "src/config/BartLambdaConfig";
import { ReminderBlock } from "src/components/reminder-block";
import { FullPageTitle } from "src/components/header/title";
import { NavigateFunction, useLocation, useNavigate } from "react-router-dom";
import { Notifications, useNotifications } from "src/pages/commons";
import { NotifyInput } from "src/pages/commons/notifications";
import { FlashbarProps } from "@amzn/awsui-components-console/flashbar";

/**
 * URL segment that pins the displayed transaction to one transactionVersion:
 * /transactionsearch/external_id/<externalId>/version/<transactionVersion>.
 */
const VERSION_SEGMENT = "version";

/** Only external id lookups return versions of a single transaction. */
const VERSIONED_ID_TYPE = "external_id";

/**
 * The fetched transactions sorted newest version first. The order matches the
 * version dropdown, so an index into this list is a dropdown selection.
 */
export const sortedVersionsOf = (payload: any): any[] => {
  if (!payload || !Array.isArray(payload.fbtsV2Response)) {
    return [];
  }
  return [...payload.fbtsV2Response].sort(
    (a: any, b: any) =>
      (b["transactionVersion"] ?? 0) - (a["transactionVersion"] ?? 0),
  );
};

/**
 * The transactionVersion pinned in the current URL, or null when the URL
 * carries no version. Versionless URLs stay supported, and a version on
 * anything other than an external id lookup is ignored.
 *
 * Returned as the raw string so a malformed value can be reported back to the
 * user as they typed it.
 */
const readPinnedVersion = (pathname: string, idType: string): string | null => {
  const segments = pathname.split("/");
  if (idType !== VERSIONED_ID_TYPE || segments[4] !== VERSION_SEGMENT) {
    return null;
  }
  return segments[5] || null;
};

interface Props {
  domain: RegionalDomain;
  /**
   * Current router pathname. Held as a prop rather than read from
   * window.location so the component re-resolves the pinned version when the
   * URL changes under it (browser back/forward).
   */
  pathname: string;
  navigate: NavigateFunction;
  notify: (input: NotifyInput) => void;
  clearNotifications: () => void;
  notifications: ReadonlyArray<FlashbarProps.MessageDefinition>;
}

export class AppTransactionSearch extends React.Component<
  Props,
  {
    domain: RegionalDomain;
    authenticating: boolean;
    isLoggedIn: boolean;
    show: boolean;
    keyword: string;
    searched: boolean;
    payload: any;
    idType: string;
    idLabel: string;
    isTest: string;
    eventType: string;
    count: string;
    startDate: string;
    endDate: string;
    ldap: string;
    selectedVersionIndex: number;
  }
> {
  authObj: AuthService;

  /**
   * Pathname this component last wrote through {@link pinVersionInUrl}. Such a
   * URL change is the tail end of a resolution the component already applied,
   * not fresh user intent, so it must not re-resolve or clear the notification
   * the resolution just raised.
   */
  selfWrittenPathname: string | null = null;

  constructor(props: Props) {
    super(props);
    this.authObj = new AuthService();
    const formatDate = (date: Date) => {
      const yyyy = date.getFullYear();
      const MM = String(date.getMonth() + 1).padStart(2, "0");
      const dd = String(date.getDate()).padStart(2, "0");
      const hh = String(date.getHours()).padStart(2, "0");
      const mm = String(date.getMinutes()).padStart(2, "0");
      const ss = String(date.getSeconds()).padStart(2, "0");

      return `${yyyy}-${MM}-${dd} ${hh}:${mm}:${ss}`;
    };

    this.state = {
      domain: this.props.domain,
      authenticating: true,
      isLoggedIn: false,
      show: false,
      keyword: window.location.pathname.split("/")[3] || "",
      searched: false,
      payload: "init",
      idType: window.location.pathname.split("/")[2] || "external_id",
      idLabel: this.setIdLabel(window.location.pathname.split("/")[2]),
      isTest: "no filter",
      eventType: "no filter",
      count: "10",
      startDate: formatDate(
        new Date(new Date().getTime() - 3 * 24 * 60 * 60 * 1000),
      ),
      endDate: formatDate(new Date()),
      ldap: "",
      selectedVersionIndex: 0,
    };
    this.setValue = this.setValue.bind(this);
    this.setCount = this.setCount.bind(this);
    this.setDateRange = this.setDateRange.bind(this);
    this.search = this.search.bind(this);
    this.callback = this.callback.bind(this);
    this.setChoice = this.setChoice.bind(this);
    this.setIsTest = this.setIsTest.bind(this);
    this.setEventType = this.setEventType.bind(this);
    this.setSelectedVersionIndex = this.setSelectedVersionIndex.bind(this);
  }

  setIdLabel(value: string): string {
    if (value === "reseller_id") {
      return "reseller id";
    }
    if (value === "subs_subscription_id") {
      return "subscription id";
    }
    return "external id";
  }

  componentDidMount() {
    const fetchData = async () => {
      // Run authentication
      await this.authObj.authenticate();
      // set loading state to false after authentication completes. This will cause the page to re-render.
      this.setState({
        authenticating: false,
        isLoggedIn: this.authObj.getIsLoggedIn(),
        ldap: this.authObj.ldap,
      });
    };
    fetchData();
    if (this.state.idType && this.state.keyword) {
      this.search(this.state.domain);
    }
  }

  /**
   * Honours a version pinned into the URL by the browser rather than by this
   * page - back/forward across entries pushed by Fetch. The transaction id is
   * unchanged in that case, so the page is not remounted and no fetch runs;
   * the already fetched versions are re-resolved against the new URL.
   */
  componentDidUpdate(prevProps: Props) {
    if (
      prevProps.pathname === this.props.pathname ||
      this.state.idType !== VERSIONED_ID_TYPE
    ) {
      return;
    }
    if (this.props.pathname === this.selfWrittenPathname) {
      this.selfWrittenPathname = null;
      return;
    }
    const sortedVersions = sortedVersionsOf(this.state.payload);
    if (sortedVersions.length === 0) {
      return;
    }
    this.props.clearNotifications();
    const selectedVersionIndex = this.resolvePinnedVersion(sortedVersions);
    if (selectedVersionIndex !== this.state.selectedVersionIndex) {
      this.setState({ selectedVersionIndex });
    }
  }

  setDateRange(startDate: string, endDate: string) {
    this.setState({ startDate });
    this.setState({ endDate });
  }

  setValue(value: string) {
    this.setState({ keyword: value.trim() });
  }

  setCount(value: string) {
    this.setState({ count: value.trim() });
  }

  setChoice(choice: string) {
    let choiceLabel: string = "";
    if (choice === "external_id") {
      choiceLabel = "external id";
    } else if (choice === "reseller_id") {
      choiceLabel = "reseller id";
    } else if (choice === "subs_subscription_id") {
      choiceLabel = "subscription id";
    }
    this.setState({ idType: choice, idLabel: choiceLabel, searched: false });
  }

  setIsTest(value: string) {
    this.setState({ isTest: value });
  }

  setEventType(value: string) {
    this.setState({ eventType: value });
  }

  /**
   * Rewrites the current URL to pin the given transactionVersion, or to drop
   * the /version segment when no version is given. The history entry is
   * replaced so switching versions does not pile up back-button steps.
   */
  pinVersionInUrl(version?: number) {
    const segments = this.props.pathname.split("/");
    // ["", "transactionsearch", <type>, <id>, ...]
    if (segments.length < 4 || !segments[3]) {
      return;
    }
    const idPath = segments.slice(0, 4).join("/");
    const nextPathname =
      version === undefined
        ? idPath
        : `${idPath}/${VERSION_SEGMENT}/${version}`;
    if (nextPathname === this.props.pathname) {
      return;
    }
    this.selfWrittenPathname = nextPathname;
    this.props.navigate(nextPathname, { replace: true });
  }

  setSelectedVersionIndex(index: number) {
    this.setState({ selectedVersionIndex: index });
    if (this.state.idType !== VERSIONED_ID_TYPE) {
      return;
    }
    const version = sortedVersionsOf(this.state.payload)[index]?.[
      "transactionVersion"
    ];
    if (version !== undefined) {
      this.pinVersionInUrl(version);
    }
  }

  /**
   * Resolves the version pinned in the URL against the versions just fetched
   * and returns the dropdown index to select.
   *
   * A pinned version that is malformed or absent from the fetched versions
   * falls back to the latest version: the /version segment is dropped from the
   * URL and an error notification explains the fallback.
   */
  resolvePinnedVersion(sortedVersions: any[]): number {
    const pinned = readPinnedVersion(this.props.pathname, this.state.idType);
    if (pinned === null || sortedVersions.length === 0) {
      return 0;
    }

    const isNumeric = /^\d+$/.test(pinned);
    const matchedIndex = isNumeric
      ? sortedVersions.findIndex(
          (transaction: any) =>
            transaction?.["transactionVersion"] === Number(pinned),
        )
      : -1;
    if (matchedIndex >= 0) {
      return matchedIndex;
    }

    const latestVersion = sortedVersions[0]?.["transactionVersion"];
    this.props.notify({
      type: "error",
      content: isNumeric
        ? `Desired transaction version (${pinned}) not found, defaulting to latest version (${latestVersion}).`
        : `Desired transaction version (${pinned}) is invalid, defaulting to latest version (${latestVersion}).`,
    });
    this.pinVersionInUrl();
    return 0;
  }

  search(domain: RegionalDomain) {
    // Banners describe the result being replaced, so drop them before fetching.
    this.props.clearNotifications();
    this.setState({
      searched: true,
      payload: "init",
      selectedVersionIndex: 0,
    });
    invokeLambda(
      this.state.idType,
      this.state.keyword,
      this.state.startDate,
      this.state.endDate,
      this.state.count,
      this.state.isTest,
      this.state.eventType,
      domain,
      this.callback,
    );
  }

  callback(result: any) {
    if (result.error) {
      // Handle error cases
      if (result.error === "error") {
        this.setState({ payload: "error" });
      } else if (result.error === "invalid") {
        this.setState({ payload: "invalid" });
      }
    } else {
      // Handle successful response
      const { fbtsV2Response, ftsResponse } = result;
      const combinedResponse = {
        fbtsV2Response,
        ftsResponse,
      };
      const selectedVersionIndex = this.resolvePinnedVersion(
        sortedVersionsOf(combinedResponse),
      );
      this.setState({ payload: combinedResponse, selectedVersionIndex });
    }
  }

  render() {
    if (this.state.authenticating || !this.state.isLoggedIn) {
      // render loading spinner
      return <div>Authenticating...</div>;
    } else {
      if (
        !(
          this.state.ldap.includes("bart-transaction") ||
          this.state.ldap.includes("bart-read-only") ||
          this.state.ldap.includes("bart-admin")
        )
      ) {
        return (
          <div>
            <FullPageTitle
              title={"Transaction Search"}
              description={"Check the detail of transactions by external id"}
            />
            <SpaceBetween size="l" />
            <Grid gridDefinition={[{ colspan: { l: 7, xxs: 10 } }]}>
              <Alert
                header="Only authorized users can perform a transaction search!"
                type="error"
              ></Alert>
            </Grid>
          </div>
        );
      }
      // render page if authentication has been processed and was successful
      let lis: string[] = [];
      if (this.state.idType === "") {
        lis.push("Please select search type first.");
      } else if (this.state.idType !== "external_id") {
        lis.push("You can search by the prefix.");
        lis.push(
          "Please select the date range and data count between 1 and 10 inclusive.",
        );
      }
      lis.push("Search is case-sensitive.");

      // Compute sorted versions and version options from the payload if available
      const sortedVersions = sortedVersionsOf(this.state.payload);
      const versionOptions = sortedVersions.map(
        (version: any, index: number) => ({
          value: index.toString(),
          label: `Version ${version["transactionVersion"] ?? index + 1}${index === 0 ? " (latest)" : ""}`,
        }),
      );

      const selectedVersion =
        sortedVersions[this.state.selectedVersionIndex] ??
        sortedVersions[0] ??
        null;

      return (
        <InitDomain.Consumer>
          {(context) => (
            <>
              <FullPageTitle
                title={"Transaction Search"}
                description={"Check the detail of transactions by external id"}
              />
              <div className={this.state.searched ? "" : "page"}>
                <SpaceBetween size="l">
                  <Notifications items={this.props.notifications} />
                  <TransactionSearchBar
                    keyword={this.state.keyword}
                    idType={this.state.idType}
                    setValue={this.setValue}
                    setChoice={this.setChoice}
                    onClickSearch={this.search}
                    domain={context.domain as RegionalDomain}
                    setDateRange={this.setDateRange}
                    count={this.state.count}
                    setCount={this.setCount}
                    idLabel={this.state.idLabel}
                    isTest={this.state.isTest}
                    setIsTest={this.setIsTest}
                    eventType={this.state.eventType}
                    setEventType={this.setEventType}
                    versionOptions={versionOptions}
                    selectedVersionIndex={this.state.selectedVersionIndex}
                    onVersionChange={this.setSelectedVersionIndex}
                  />
                  {this.state.searched ? (
                    <PayloadDisplay
                      searchResults={this.state.payload}
                      searchId={this.state.keyword}
                      selectedVersion={selectedVersion}
                    />
                  ) : (
                    <ReminderBlock title="Information" lis={lis} />
                  )}
                </SpaceBetween>
              </div>
            </>
          )}
        </InitDomain.Consumer>
      );
    }
  }
}

/**
 * Supplies the router and notification hooks {@link AppTransactionSearch}
 * needs; it is a class component, so it cannot call them itself.
 */
export const TransactionSearchPage = (props: { domain: Domain }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { notifications, notify, clearNotifications } = useNotifications();
  return (
    <AppTransactionSearch
      domain={props.domain as RegionalDomain}
      pathname={pathname}
      navigate={navigate}
      notify={notify}
      clearNotifications={clearNotifications}
      notifications={notifications}
    />
  );
};

export default TransactionSearchPage;
